import React, { useState, useMemo } from 'react';
import { Rocket, Plus, RefreshCw, X, ChevronRight, CheckCircle2, XCircle, Clock, Edit2, Trash2, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, Search, Download, Upload, Copy } from 'lucide-react';
import { getColorClasses } from '../utils/colors';

const DeploymentManager = ({
  products,
  deploymentStats,
  selectedProduct,
  branchFilter,
  branchNameFilter,
  productSearchQuery,
  groupFilter,
  editingDeploymentType,
  groups,
  setSelectedProduct,
  setBranchFilter,
  setBranchNameFilter,
  setProductSearchQuery,
  setGroupFilter,
  setEditingDeploymentType,
  setSelectedDeployment,
  setShowAddProductModal,
  setShowEditProductModal,
  setEditingProduct,
  setShowDeleteConfirmModal,
  setProductToDelete,
  updateDeploymentType,
  fetchDeployments,
  fetchDeploymentStats,
  fetchGroups,
  handleExportProducts,
  handleImportProducts,
  handleCloneProduct
}) => {
  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date'); // Only 'date' sorting
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = oldest first, 'desc' = newest first
  const ITEMS_PER_PAGE = 25;
  
  // Reset pagination when product or filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedProduct, branchFilter, groupFilter, branchNameFilter, productSearchQuery]);

  // Get current product
  const product = selectedProduct ? products.find(p => p.id === selectedProduct) : null;

  // Get unique branches based on current group filter
  // This shows only branches from products in the selected group
  const allUniqueBranches = React.useMemo(() => {
    const branchSet = new Set();
    
    // Filter products by group first
    const filteredProducts = products.filter(product => {
      if (groupFilter === 'all') return true;
      if (groupFilter === 'none') return !product.group_id;
      return product.group_id === parseInt(groupFilter);
    });
    
    // Collect branches only from filtered products
    filteredProducts.forEach(product => {
      product.deployments.forEach(deployment => {
        if (deployment.branch) {
          branchSet.add(deployment.branch);
        }
      });
    });
    
    return Array.from(branchSet).sort();
  }, [products, groupFilter]); // Depends on products AND groupFilter

  // Get unique branches
  const uniqueBranches = React.useMemo(() => {
    if (!product) return [];
    return [...new Set(
      product.deployments
        .map(d => d.branch)
        .filter(branch => branch)
    )].sort();
  }, [product]);

  // Filter deployments by selected branch
  const filteredDeployments = React.useMemo(() => {
    if (!product) return [];
    return branchFilter === 'all' 
      ? product.deployments 
      : product.deployments.filter(d => d.branch === branchFilter);
  }, [product, branchFilter]);

  // Sort deployments
  const sortedDeployments = useMemo(() => {
    const sorted = [...filteredDeployments].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          // Parse dates consistently without timezone conversion
          const dateStrA = a.details?.date_time || a.time;
          const dateStrB = b.details?.date_time || b.time;
          // Handle null/undefined dates safely
          if (!dateStrA && !dateStrB) return 0;
          if (!dateStrA) return 1;
          if (!dateStrB) return -1;
          const dateA = new Date(typeof dateStrA === 'string' ? dateStrA.replace(' ', 'T') : dateStrA);
          const dateB = new Date(typeof dateStrB === 'string' ? dateStrB.replace(' ', 'T') : dateStrB);
          comparison = dateB - dateA; // Newest first by default
          break;
        case 'tag':
          comparison = (a.tag || '').localeCompare(b.tag || '');
          break;
        case 'status':
          const statusOrder = { 'Success': 1, 'Pending': 2, 'Failed': 3 };
          const statusA = statusOrder[a.status] || 999;
          const statusB = statusOrder[b.status] || 999;
          comparison = statusA - statusB;
          break;
        case 'type':
          const typeOrder = { 'live': 1, 'previous': 2, null: 3 };
          const typeA = typeOrder[a.deployment_type] || 3;
          const typeB = typeOrder[b.deployment_type] || 3;
          comparison = typeA - typeB;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [filteredDeployments, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(sortedDeployments.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedDeployments = sortedDeployments.slice(startIndex, endIndex);

  // Sort toggle function
  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc'); // Default to ascending
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  // Render product detail view
  if (selectedProduct) {
    if (!product) return null;

    return (
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={() => {
              setSelectedProduct(null);
              setBranchFilter('all');
            }}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-2"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span className="text-blue-400">All Products</span>
            <span>/</span>
            <span>{product.name}</span>
          </div>
        </div>

        {/* Product Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Rocket className="w-6 h-6 text-blue-400" />
            <h1 className="text-2xl font-bold">Docker Tags for {product.name}</h1>
            <span className="text-gray-400 text-sm">({sortedDeployments.length} {branchFilter === 'all' ? 'total' : 'filtered'} tags)</span>
          </div>
          
          {/* Controls Row: Branch Filter + Sort */}
          <div className="flex items-center justify-between mt-4 gap-4">
            {/* Branch Filter */}
            {uniqueBranches.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-400">Filter by Branch:</label>
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                >
                  <option value="all">All Branches ({product.deployments.length})</option>
                  {uniqueBranches.map((branch) => {
                    const count = product.deployments.filter(d => d.branch === branch).length;
                    return (
                      <option key={branch} value={branch}>
                        {branch} ({count})
                      </option>
                    );
                  })}
                </select>
                {branchFilter !== 'all' && (
                  <button
                    onClick={() => setBranchFilter('all')}
                    className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear Filter
                  </button>
                )}
              </div>
            )}

            {/* Sort Controls */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-400">Sort by:</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSort('date')}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 bg-blue-500/20 text-blue-400 border border-blue-500/30"
                >
                  Date {getSortIcon('date')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Deployment List */}
        <div className="space-y-3">
          {paginatedDeployments.length === 0 ? (
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center">
                  <Rocket className="w-8 h-8 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-400 mb-1">No deployments found</h3>
                  <p className="text-sm text-gray-500">
                    {branchFilter === 'all' 
                      ? 'No Docker tags available for this product yet.'
                      : `No deployments found for branch "${branchFilter}".`
                    }
                  </p>
                </div>
                {branchFilter !== 'all' && (
                  <button
                    onClick={() => setBranchFilter('all')}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    View All Branches
                  </button>
                )}
              </div>
            </div>
          ) : (
            paginatedDeployments.map((deployment, index) => (
              <div
                key={index}
                onClick={() => setSelectedDeployment(deployment)}
                className="bg-gray-900 rounded-lg border border-gray-800 p-4 hover:border-gray-700 transition-colors group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <Rocket className="w-5 h-5 text-blue-400" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold">{deployment.tag}</h3>
                        
                        {/* Editable Deployment Type Badge */}
                        {editingDeploymentType && editingDeploymentType.id === deployment.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateDeploymentType(deployment.id, 'live');
                              }}
                              className={`px-2 py-0.5 border rounded text-xs font-semibold uppercase transition-colors ${
                                deployment.deployment_type === 'live'
                                  ? 'bg-red-500/40 text-red-300 border-red-400 ring-2 ring-red-500/50'
                                  : 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                              }`}
                            >
                              LIVE
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateDeploymentType(deployment.id, 'previous');
                              }}
                              className={`px-2 py-0.5 border rounded text-xs font-semibold uppercase transition-colors ${
                                deployment.deployment_type === 'previous'
                                  ? 'bg-blue-500/40 text-blue-300 border-blue-400 ring-2 ring-blue-500/50'
                                  : 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30'
                              }`}
                            >
                              PREVIOUS
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateDeploymentType(deployment.id, null);
                              }}
                              className={`px-2 py-0.5 border rounded text-xs font-semibold uppercase transition-colors ${
                                !deployment.deployment_type
                                  ? 'bg-gray-500/40 text-gray-300 border-gray-400 ring-2 ring-gray-500/50'
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30 hover:bg-gray-500/30'
                              }`}
                            >
                              CLEAR
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDeploymentType(null);
                              }}
                              className="px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded text-xs hover:bg-gray-600 transition-colors"
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            {deployment.deployment_type && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDeploymentType({ id: deployment.id, currentType: deployment.deployment_type });
                                }}
                                className="group/badge relative"
                              >
                                {deployment.deployment_type === 'live' ? (
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-xs font-semibold uppercase flex items-center gap-1 group-hover/badge:bg-red-500/30 transition-colors">
                                    <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></span>
                                    LIVE
                                  </span>
                                ) : deployment.deployment_type === 'previous' ? (
                                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-xs font-semibold uppercase group-hover/badge:bg-blue-500/30 transition-colors">
                                    PREVIOUS
                                  </span>
                                ) : null}
                              </button>
                            )}
                            {!deployment.deployment_type && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDeploymentType({ id: deployment.id, currentType: null });
                                }}
                                className="px-2 py-0.5 bg-gray-800 text-gray-500 border border-gray-700 rounded text-xs font-medium hover:bg-gray-700 hover:text-gray-400 transition-colors"
                                title="Click to set deployment type"
                              >
                                + Set Type
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                          </svg>
                          {deployment.branch}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {deployment.details && deployment.details.date_time ? (() => {
                            // Parse the date string as-is without timezone conversion
                            const dateStr = deployment.details.date_time;
                            const date = new Date(dateStr.replace(' ', 'T')); // Convert to ISO format
                            return date.toLocaleString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            });
                          })() : (deployment.time || 'No date')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {deployment.status && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                        deployment.status === 'Success' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        deployment.status === 'Failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                        'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }`}>
                        {deployment.status === 'Success' && (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4" />
                            Success
                          </span>
                        )}
                        {deployment.status === 'Failed' && (
                          <span className="flex items-center gap-1">
                            <XCircle className="w-4 h-4" />
                            Failed
                          </span>
                        )}
                        {deployment.status === 'Pending' && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Pending
                          </span>
                        )}
                      </span>
                    )}
                    {deployment.environment && (
                      <span className={`px-3 py-1 rounded text-sm ${
                        deployment.environment === 'Production' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {deployment.environment}
                      </span>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Showing {startIndex + 1} to {Math.min(endIndex, sortedDeployments.length)} of {sortedDeployments.length} tags
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = 
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);
                  
                  const showEllipsis = 
                    (page === 2 && currentPage > 3) ||
                    (page === totalPages - 1 && currentPage < totalPages - 2);

                  if (showEllipsis) {
                    return <span key={page} className="px-3 py-2 text-gray-500">...</span>;
                  }

                  if (!showPage) return null;

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render product list view
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Rocket className="w-8 h-8 text-blue-400" />
            <div>
              <h1 className="text-2xl font-bold">Deployment History</h1>
              <p className="text-sm text-gray-400">All Products • Monitor your CI/CD pipeline</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (fetchGroups) fetchGroups();
                setShowAddProductModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
            <button
              onClick={handleExportProducts}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              title="Export all products and builds"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handleImportProducts}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              title="Import products and builds"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            onClick={() => {
              fetchDeployments();
              fetchDeploymentStats();
            }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm placeholder-gray-500 min-w-[200px]"
            />
            {productSearchQuery && (
              <button
                onClick={() => setProductSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Group Filter */}
          {groups && groups.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Group:</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
              >
                <option value="all">All Groups ({products.length})</option>
                {groups.map((group) => {
                  const count = products.filter(p => p.group_id === group.id).length;
                  return (
                    <option key={group.id} value={group.id}>
                      {group.name} ({count})
                    </option>
                  );
                })}
                <option value="none">No Group ({products.filter(p => !p.group_id).length})</option>
              </select>
            </div>
          )}

          {/* Multi-select Branch Name Filter */}
          {allUniqueBranches.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Branches:</label>
              <div className="relative">
                <select
                  multiple={false}
                  value=""
                  onChange={(e) => {
                    const branch = e.target.value;
                    if (branch && !branchNameFilter.includes(branch)) {
                      setBranchNameFilter([...branchNameFilter, branch]);
                    }
                  }}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm min-w-[150px]"
                >
                  <option value="">Add branch...</option>
                  {allUniqueBranches
                    .filter(branch => !branchNameFilter.includes(branch))
                    .map((branch) => {
                      const count = products.filter(p => 
                        p.deployments.some(d => d.branch === branch)
                      ).length;
                      return (
                        <option key={branch} value={branch}>
                          {branch} ({count})
                        </option>
                      );
                    })}
                </select>
              </div>
              {branchNameFilter.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {branchNameFilter.map((branch) => (
                    <span
                      key={branch}
                      className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-xs font-semibold flex items-center gap-1"
                    >
                      {branch}
                      <button
                        onClick={() => setBranchNameFilter(branchNameFilter.filter(b => b !== branch))}
                        className="hover:text-blue-300"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Clear All Filters Button */}
          {(groupFilter !== 'all' || branchNameFilter.length > 0 || productSearchQuery) && (
            <button
              onClick={() => {
                setGroupFilter('all');
                setBranchNameFilter([]);
                setProductSearchQuery('');
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* All Products Section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-300">All Products</h2>
            {(groupFilter !== 'all' || branchNameFilter.length > 0 || productSearchQuery) && (
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full text-xs font-semibold">
                {
                  [
                    productSearchQuery && 'Search',
                    groupFilter !== 'all' && 'Group',
                    branchNameFilter.length > 0 && `${branchNameFilter.length} Branch${branchNameFilter.length > 1 ? 'es' : ''}`
                  ].filter(Boolean).join(' + ')
                } Active
              </span>
            )}
          </div>
          <span className="text-sm text-gray-400">
            Showing {products.filter((product) => {
              // Group filter
              let groupMatch = true;
              if (groupFilter === 'all') {
                groupMatch = true;
              } else if (groupFilter === 'none') {
                groupMatch = !product.group_id;
              } else {
                groupMatch = product.group_id === parseInt(groupFilter);
              }
              
              // Branch filter (multi-select)
              let branchMatch = true;
              if (branchNameFilter.length > 0) {
                branchMatch = product.deployments.some(d => branchNameFilter.includes(d.branch));
              }
              
              // Search filter
              let searchMatch = true;
              if (productSearchQuery) {
                searchMatch = product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                             (product.description && product.description.toLowerCase().includes(productSearchQuery.toLowerCase()));
              }
              
              return groupMatch && branchMatch && searchMatch;
            }).length} of {products.length} products
          </span>
        </div>
        <div className="space-y-3">
          {products
            .filter((product) => {
              // Filter by group
              let groupMatch = true;
              if (groupFilter === 'all') {
                groupMatch = true;
              } else if (groupFilter === 'none') {
                groupMatch = !product.group_id;
              } else {
                groupMatch = product.group_id === parseInt(groupFilter);
              }
              
              // Filter by branch name (multi-select)
              let branchMatch = true;
              if (branchNameFilter.length > 0) {
                branchMatch = product.deployments.some(d => branchNameFilter.includes(d.branch));
              }
              
              // Filter by search query
              let searchMatch = true;
              if (productSearchQuery) {
                searchMatch = product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                             (product.description && product.description.toLowerCase().includes(productSearchQuery.toLowerCase()));
              }
              
              // All filters must match
              return groupMatch && branchMatch && searchMatch;
            })
            .map((product) => {
            const colors = getColorClasses(product.color);
            return (
              <div
                key={product.id}
                className={`bg-gray-900 rounded-lg border p-4 transition-all ${colors.border}`}
              >
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer"
                    onClick={() => setSelectedProduct(product.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold">{product.name}</h3>
                        {product.group_name && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs font-semibold">
                            {product.group_name}
                          </span>
                        )}
                        {product.deployments.some(d => d.deployment_type === 'live') && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-xs font-semibold uppercase flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></span>
                            LIVE
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Docker Tags:</span>
                          <span className={`font-semibold ${colors.text}`}>{product.dockerTags}</span>
                        </div>
                        {product.totalDeployments > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">Total Deployments:</span>
                            <span className="font-semibold text-gray-300">{product.totalDeployments}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (fetchGroups) fetchGroups();
                        handleCloneProduct(product);
                      }}
                      className="p-2 hover:bg-gray-800 rounded transition-colors"
                      title="Clone Product"
                    >
                      <Copy className="w-4 h-4 text-blue-400 hover:text-blue-300" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (fetchGroups) fetchGroups();
                        setEditingProduct({
                          id: product.id,
                          name: product.name,
                          description: product.description || '',
                          color: product.color || 'blue',
                          version_url: product.version_url || '',
                          group_id: product.group_id
                        });
                        setShowEditProductModal(true);
                      }}
                      className="p-2 hover:bg-gray-800 rounded transition-colors"
                      title="Edit Product"
                    >
                      <Edit2 className="w-4 h-4 text-gray-400 hover:text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProductToDelete(product);
                        setShowDeleteConfirmModal(true);
                      }}
                      className="p-2 hover:bg-gray-800 rounded transition-colors"
                      title="Delete Product"
                    >
                      <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DeploymentManager;
